/**
 * This is the ONLY authoritative Analysis page component.
 * Do not duplicate analysis UI elsewhere.
 * 
 * Route: /analysis/:analysisId
 * 
 * This page fetches analysis data from swing_runs.analysis_data_v3 ONLY.
 * No fallback to swing_analysis_runs or other tables.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SwingAnalysisCanon } from '@/features/analysis/components/SwingAnalysisCanon';
import { AiAnalysisResponse, AiTelemetry, EnrichedAnalysisV1 } from '@/features/swing-lab/ai/types';

export default function AnalysisPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<AiAnalysisResponse | EnrichedAnalysisV1 | null>(null);
  const [telemetry, setTelemetry] = useState<AiTelemetry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [analyzedAt, setAnalyzedAt] = useState<string>('');
  // PLA-111: Coaching data for LLM-generated focus title
  const [coachingData, setCoachingData] = useState<any>(null);
  /** Pose/telemetry data for overlay on video (skeleton) */
  const [poseData, setPoseData] = useState<import('@/features/telemetry2/types').PoseData | null>(null);

  useEffect(() => {
    if (!analysisId) {
      setError('Analysis ID is required');
      setLoading(false);
      return;
    }

    async function fetchAnalysis() {
      try {
        setLoading(true);
        setError(null);

        // Check authentication first
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('[AnalysisPage] User not authenticated:', authError);
          setError('You must be logged in to view analysis');
          setLoading(false);
          return;
        }

        console.log('[AnalysisPage] Fetching analysis:', { analysisId, userId: user.id });

        // PHASE 0.5: Query ONLY swing_runs.analysis_data_v3 (canonical data source)
        // PLA-111: Also fetch coaching_data_v3 for LLM-generated focus title
        // NO fallback to swing_analysis_runs or other tables
        const { data: swingRun, error: swingError } = await supabase
          .from('swing_runs')
          .select('analysis_data_v3, coaching_data_v3, cf_playback_url, created_at, updated_at, user_id, status, pose_data, pose_raw_data')
          .eq('id', analysisId)
          .single();

        if (swingError) {
          console.error('[AnalysisPage] Run query error:', {
            code: swingError.code,
            message: swingError.message,
            details: swingError.details,
            hint: swingError.hint,
            analysisId,
            userId: user.id
          });
          
          // If run not found, try to redirect to user's most recent run
          if (swingError.code === 'PGRST116') {
            console.log('[AnalysisPage] Run not found, attempting to find user\'s most recent run...');
            
            // Try to find user's most recent run with analysis
            const { data: recentRuns, error: recentError } = await supabase
              .from('swing_runs')
              .select('id, status, analysis_data_v3, created_at')
              .eq('user_id', user.id)
              .not('analysis_data_v3', 'is', null)
              .order('created_at', { ascending: false })
              .limit(1);
            
            if (!recentError && recentRuns && recentRuns.length > 0) {
              const mostRecentRun = recentRuns[0];
              console.log('[AnalysisPage] Redirecting to most recent run:', mostRecentRun.id);
              navigate(`/analysis/${mostRecentRun.id}`, { replace: true });
              return;
            }
            
            // No recent runs found, show error
            setError(`Analysis not found. The run ID "${analysisId}" does not exist.`);
          } else {
            setError(swingError.message || 'Failed to load analysis');
          }
          setLoading(false);
          return;
        }

        if (!swingRun) {
          console.error('[AnalysisPage] Run not found (null result):', { analysisId, userId: user.id });
          
          // Try to find user's most recent run
          const { data: recentRuns, error: recentError } = await supabase
            .from('swing_runs')
            .select('id, status, analysis_data_v3, created_at')
            .eq('user_id', user.id)
            .not('analysis_data_v3', 'is', null)
            .order('created_at', { ascending: false })
            .limit(1);
          
          if (!recentError && recentRuns && recentRuns.length > 0) {
            const mostRecentRun = recentRuns[0];
            console.log('[AnalysisPage] Redirecting to most recent run:', mostRecentRun.id);
            navigate(`/analysis/${mostRecentRun.id}`, { replace: true });
            return;
          }
          
          setError(`Analysis not found. The run ID "${analysisId}" does not exist.`);
          setLoading(false);
          return;
        }

        // Check if user owns this run (RLS should handle this, but double-check for better error message)
        if (swingRun.user_id !== user.id) {
          console.error('[AnalysisPage] Permission denied:', {
            analysisId,
            runUserId: swingRun.user_id,
            currentUserId: user.id
          });
          setError('You do not have permission to view this analysis');
          setLoading(false);
          return;
        }

        if (!swingRun.analysis_data_v3) {
          setError('No V3 analysis data available for this run');
          setLoading(false);
          return;
        }

        // Validate video URL - missing videoUrl is a fatal data error
        if (!swingRun.cf_playback_url) {
          setError('Video URL is missing. This analysis cannot be displayed.');
          setLoading(false);
          return;
        }

        // Set analysis from canonical source
        setAnalysis(swingRun.analysis_data_v3 as AiAnalysisResponse | EnrichedAnalysisV1);
        setTelemetry(null); // swing_runs doesn't have telemetry_ai column
        
        // PLA-111: Set coaching data for LLM-generated focus title
        if (swingRun.coaching_data_v3) {
          setCoachingData(swingRun.coaching_data_v3);
        }

        // Pose data for overlay must be MediaPipe-style 0–1 image coordinates. The worker stores
        // pose_data as processedLandmarks (hip-centered offsets) — wrong for overlay. Use pose_raw_data
        // when present (raw MediaPipe output). Fallback to pose_data only for legacy shapes that are still 0–1.
        type FrameEntry = { timestamp_ms: number; pose_landmarks: Array<{ x: number; y: number; z: number; visibility: number }> };
        const defaultMeta = { width: 1920, height: 1080, durationSec: 0 };
        let frames: FrameEntry[] = [];

        const frameFromWorkerFrame = (f: { timestamp?: number; frameIndex?: number; landmarks?: Array<{ x: number; y: number; z: number; visibility: number }> }): FrameEntry | null => {
          if (!Array.isArray(f.landmarks) || f.landmarks.length < 33) return null;
          const ms = typeof f.timestamp === 'number' ? f.timestamp * 1000 : (f.frameIndex ?? 0) * 33.33;
          return { timestamp_ms: ms, pose_landmarks: f.landmarks };
        };

        // 1) Prefer pose_raw_data (raw landmarks = 0–1, aligns with video)
        const rawData = (swingRun as { pose_raw_data?: unknown }).pose_raw_data;
        if (Array.isArray(rawData)) {
          for (const item of rawData) {
            if (item && typeof item === 'object' && item !== null) {
              const fe = frameFromWorkerFrame(item as any);
              if (fe) frames.push(fe);
            }
          }
        }

        // 2) Legacy pose_data object shapes only (not v2 flat array — that is hip-centered processedLandmarks)
        const poseDataOnly = swingRun.pose_data;
        if (frames.length === 0 && poseDataOnly != null && typeof poseDataOnly === 'object' && !Array.isArray(poseDataOnly)) {
          const pd = poseDataOnly as Record<string, unknown>;
          if (Array.isArray(pd.landmarks)) {
            const arr = pd.landmarks as Array<{ timestamp_ms?: number; timestamp?: number; pose_landmarks?: unknown; landmarks?: unknown }>;
            for (const f of arr) {
              const ms = typeof f.timestamp_ms === 'number' ? f.timestamp_ms : typeof f.timestamp === 'number' ? f.timestamp * 1000 : 0;
              const pl = (f.pose_landmarks ?? f.landmarks) as Array<{ x: number; y: number; z: number; visibility: number }> | null;
              if (Array.isArray(pl) && pl.length >= 33) frames.push({ timestamp_ms: ms, pose_landmarks: pl });
            }
          } else if (Array.isArray(pd.pose?.landmarks)) {
            const arr = (pd.pose as { landmarks: unknown[] }).landmarks;
            for (const f of arr as any[]) {
              const ms = typeof f.timestamp_ms === 'number' ? f.timestamp_ms : (f.timestamp ?? 0) * 1000;
              const pl = Array.isArray(f.pose_landmarks) ? f.pose_landmarks : f.landmarks;
              if (Array.isArray(pl) && pl.length >= 33) frames.push({ timestamp_ms: ms, pose_landmarks: pl });
            }
          }
        }

        if (frames.length > 0) {
          setPoseData({
            meta: defaultMeta,
            landmarks: frames as import('@/features/telemetry2/types').PoseData['landmarks'],
          });
        } else {
          setPoseData(null);
        }
        
        // Set video URL and analyzed date
        setVideoUrl(swingRun.cf_playback_url);
        setAnalyzedAt(swingRun.updated_at || swingRun.created_at || '');
        
        setLoading(false);
      } catch (err) {
        console.error('[AnalysisPage] Unexpected error:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        setLoading(false);
      }
    }

    fetchAnalysis();
  }, [analysisId]);

  // #region agent log
  useEffect(() => {
    fetch('http://127.0.0.1:7243/ingest/33cdab5a-28bd-4c6b-a49e-83c5415715b9',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AnalysisPage.tsx:125',message:'Render state',data:{loading,hasError:!!error,hasAnalysis:!!analysis,analysisId},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'H5'})}).catch(()=>{});
  }, [loading, error, analysis, analysisId]);
  // #endregion

  if (loading) {
    return (
      <div className="bg-background flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/33cdab5a-28bd-4c6b-a49e-83c5415715b9',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AnalysisPage.tsx:138',message:'Rendering error state',data:{error,hasAnalysis:!!analysis},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'H5'})}).catch(()=>{});
    // #endregion
    return (
      <div className="bg-background flex items-center justify-center py-24">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold mb-4">Analysis Not Found</h1>
          <p className="text-muted-foreground mb-6">{error || 'The analysis you\'re looking for doesn\'t exist.'}</p>
          <button
            onClick={() => navigate('/videos')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Back to Videos
          </button>
        </div>
      </div>
    );
  }

  // #region agent log
  const analysisKeys = analysis ? Object.keys(analysis) : [];
  const hasTaxonomyVersion = analysis && 'taxonomy_version' in analysis;
  const hasPhaseSummaries = analysis && 'phase_summaries' in analysis;
  const hasFlaws = analysis && 'flaws' in analysis;
  console.log('[DEBUG] Rendering AiAnalysisResults', { hasAnalysis: !!analysis, hasTelemetry: !!telemetry, runId: analysisId, analysisKeys, hasTaxonomyVersion, hasPhaseSummaries, hasFlaws });
  fetch('http://127.0.0.1:7243/ingest/33cdab5a-28bd-4c6b-a49e-83c5415715b9',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AnalysisPage.tsx:152',message:'Rendering AiAnalysisResults',data:{hasAnalysis:!!analysis,hasTelemetry:!!telemetry,runId:analysisId,analysisKeys,hasTaxonomyVersion,hasPhaseSummaries,hasFlaws,analysisType:typeof analysis},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'H6'})}).catch(()=>{});
  // #endregion
  return (
    <SwingAnalysisCanon
      analysis={analysis}
      runId={analysisId!}
      videoUrl={videoUrl}
      analyzedAt={analyzedAt}
      coachingData={coachingData}
      poseData={poseData ?? undefined}
    />
  );
}

